/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {translate} from '@ion/core/src/locale/locale';
import {CVSAssetType, SCDeviceType} from '@ion/core/src/types/entities';
import {BaseMapEnum, LabelContentType} from '@ion/dls/src/types/types';
import {dlsColor} from '../../theme/theme';
import {getItemQuery} from './MapViewUtils';

type LabelClassProps = {
  property?: LabelContentType,
  defaultProperty?: string,
  basemap?: BaseMapEnum,
  isRenderer?: boolean,
  defaultColor?: boolean,
  where?: string,
  labelContent?: LabelContentType,
  defaultLabelContent?: string
}

type LabelClassQueryProps = {
  selectedItem: SCDeviceType | CVSAssetType,
  type: string,
  property: string,
  defaultQuery?: string
};

export type LabelStyle = {
  color: any;
  haloColor: any;
}

export enum Offset {
  CIM = 0,
  CVS = -4
}

export enum LabelTheme {
  light = 'light',
  dark = 'dark'
}

export const labelThemesWithOpacity = {
  [LabelTheme.light] : {
    color     : [51, 51, 51, 0.85], // grey1 with 15% opacity
    haloColor : [255, 255, 255, 0.4] // white with 60% opacity
  },
  [LabelTheme.dark] : {
    color     : [255, 255, 255, 0.8], // white with 20% opacity
    haloColor : [51, 51, 51, 0.4] // grey1 with 60% opacity
  }
};

export type LabelThemes = {
  [LabelTheme.light]: LabelStyle
  [LabelTheme.dark]: LabelStyle
}

// basemapLabelThemes provides a map of basemaps for the correct label themes.
export const basemapLabelThemes = {
  [BaseMapEnum.osm]                     : LabelTheme.dark,
  [BaseMapEnum.hybrid]                  : LabelTheme.light,
  [BaseMapEnum.satellite]               : LabelTheme.light,
  [BaseMapEnum.topoVector]              : LabelTheme.dark,
  [BaseMapEnum.grayVector]              : LabelTheme.dark,
  [BaseMapEnum.streetsNavigationVector] : LabelTheme.dark
};

// getLabelThemes defines text styles for labels on the map.
export const generateLabelThemes = (defaultColor: boolean = true): LabelThemes => {
  if (defaultColor) {
    const white = dlsColor('white');
    const grey  = dlsColor('grey1');
    return {
      [LabelTheme.light] : {
        color     : grey,
        haloColor : white
      },
      [LabelTheme.dark] : {
        color     : white,
        haloColor : grey
      }
    };
  };
  return labelThemesWithOpacity;
};

export const getLabelTheme = (basemap: BaseMapEnum, defaultColor: boolean = true) => {
  const themes = generateLabelThemes(defaultColor);
  const theme = basemapLabelThemes[basemap] || LabelTheme.light;
  return themes[theme];
};

/**
 * Defines a utility to returns the query for Label Class for given parameters.
 *
 * ```typescript
 * import {getLabelClassQuery} from '../helpers/LabelUtils';
 *
 * const query = getLabelClassQuery({
 *   type: 'isCvsAsset',
 *   property: 'assetId',
 *   value: '1'
 *   selectedItem
 * });
 * ```
 */
export const getLabelClassQuery = ({
  type ='isCvsAsset',
  property ='assetId',
  defaultQuery = '1 = 0',
  selectedItem
}: LabelClassQueryProps) => {
  let query = null;
  if (selectedItem) {
    if (selectedItem?.[type] && selectedItem?.[property]) {
      query = getItemQuery({item: selectedItem, property, defaultQuery});
    } else {
      query = defaultQuery;
    }
  }
  return query;
};

/**
 * Defines a utility to returns the appropriate label class for given parameters.
 *
 * ```typescript
 * import {getLayerLabelClass} from '../helpers/LabelUtils';
 *
 * const createAssetLayer = () => {
 *   const layer = new FeatureLayer({
 *     labelingInfo : [getLayerLabelClass(where, labelContent, basemap, ...)]
 *   });
 * };
 * ```
 */
export const getLayerLabelClass = ({
  where,
  labelContent,
  defaultLabelContent,
  isRenderer,
  basemap,
  defaultColor
}: LabelClassProps) => {
  const params = {
    property        : labelContent,
    defaultProperty : defaultLabelContent,
    isRenderer,
    basemap
  };
  if (where) {
    const selectedLabel = getLabelClass({
      ...params,
      where,
      defaultColor
    });
    return [selectedLabel];
  } else {
    return [getLabelClass(params)];
  }
};

/**
 * @generateLabelExpression
 * Generates the marker label based on the presence of primary and secondary labels.
 *
 * @param {string} primaryLabel - The field name of the primary label.
 * @param {string} secondaryLabel - The field name of the secondary label.
 * @returns {string} - The concatenated label expression.
 *
 * This function checks if the primary and secondary labels have values. It will
 * return an expression to display the labels according to the following rules:
 *   - If both labels are null or undefined, display 'unassigned'.
 *   - If only the primary label has a value, display it with 'unassigned' (e.g., 'assetName / unassigned').
 *   - If only the secondary label has a value, display 'unassigned' with it (e.g., 'unassigned / operatorName').
 *   - If both primary and secondary labels have values, concatenate them (e.g., 'assetName / operatorName').
 *
 * The function utilizes Arcade's IIF and IsEmpty functions for conditional logic.
 * The IIF function is a shorthand for the immediate if and behaves similarly to the ternary operator in JavaScript.
 * IsEmpty checks if a given variable is null, undefined, or an empty string.
 *
 * Reference for IIF: https://developers.arcgis.com/arcade/function-reference/logical_functions/#iif
 */
export const generateLabelExpression = (primaryLabel: string, secondaryLabel: string) => {
  const unassigned = `'${translate('maps:popup.unAssigned')}'`;
  const expression = `
    Concatenate([
      IIF(IsEmpty($feature.${primaryLabel}), ${unassigned}, $feature.${primaryLabel}),
      " / ",
      IIF(IsEmpty($feature.${secondaryLabel}), ${unassigned}, $feature.${secondaryLabel})
    ])
  `;
  return expression;
};

/**
 * @parseLabelContent
 * Parses the label content to determine if the label is a string or an object.
 *
 * @param {string | LabelContentType} labelContent - The label content to be parsed.
 * @returns {LabelContentType} - The parsed label content.
 */
const parseLabelContent = (labelContent: LabelContentType) => {
  if (typeof labelContent === 'string') {
    return {primaryLabel: labelContent, secondaryLabel: null};
  }
  if ('primaryLabel' in labelContent && 'secondaryLabel' in labelContent) {
    return labelContent;
  }
  return {primaryLabel: null, secondaryLabel: null};
};

/**
 * getLabelClass returns the appropriate label class for the basemap.
 *
 * ```typescript
 * import {getLabelClass} from '../helpers/LabelUtils';
 *
 * const createAssetLayer = () => {
 *  const layer = new FeatureLayer({
 *    labelingInfo : [getLabelClass(labelContent, basemap)]
 *  });
 * };
 * ```
 *
 * @param property The property that will be used to display the label.
 * @param basemap The basemap that has been selected from the basemap gallery.
 * @returns The label theme for the chosen basemap.
 */
const getLabelClass = ({
  property = {primaryLabel: 'assetName', secondaryLabel: null},
  isRenderer = false,
  defaultColor = true,
  where = '1 = 1',
  defaultProperty,
  basemap
}: LabelClassProps) => {
  const {primaryLabel, secondaryLabel} = parseLabelContent(property);

  const defaultExpression = defaultProperty ? `$feature.${defaultProperty}` : `'${translate('maps:popup.unAssigned')}'`;
  const singleExpression  = `DefaultValue($feature.${primaryLabel}, ${defaultExpression})`;
  const labelExpression   = secondaryLabel ? generateLabelExpression(primaryLabel, secondaryLabel) : singleExpression;

  const labelClass = {
    symbol : {
      ...getLabelTheme(basemap, defaultColor),
      type     : 'text',
      yoffset  : isRenderer ? Offset.CVS : Offset.CIM,
      haloSize : '2px',
      font     : {
        family : 'Arial',
        size   : 12,
        weight : 'bold',
        style  : 'italic'
      }
    },
    labelPlacement      : 'above-center',
    labelExpressionInfo : {
      expression : labelExpression
    },
    where
  };

  return labelClass;
};
